import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useState, useEffect, useRef } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle, ExternalLink, Store } from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import DealCheckCard from "@/components/DealCheckCard";
import AppDownloadPrompt from "@/components/AppDownloadPrompt";

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  read: boolean;
  kind?: string;
  meta?: { deal_check_id?: string } | null;
}

interface Conversation {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  created_at: string;
  listing?: { title: string; images: string[]; price: number; price_unit: string };
  other_user?: { id: string; name: string };
  unreadCount?: number;
}

/** avisa o sininho que mensagens foram lidas */
const announceRead = () => window.dispatchEvent(new Event("agro:messages-read"));

export default function MessagesPage() {
  const { user, authLoading } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const newListingId = searchParams.get("listing");
  const newSellerId = searchParams.get("seller");
  const wantedConv = searchParams.get("c");

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [pendingNew, setPendingNew] = useState<{ listingId: string; sellerId: string } | null>(null);
  const [pendingListing, setPendingListing] = useState<{ title: string; images: string[]; price: number; price_unit: string } | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [storeSlug, setStoreSlug] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);

  usePullToRefresh(fetchConversations);

  useEffect(() => { if (user) fetchConversations(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  // aviso tocado (ou link do sininho) com ?c= enquanto a pagina ja esta aberta
  useEffect(() => {
    if (!wantedConv || !conversations.length || selected?.id === wantedConv) return;
    const c = conversations.find((x) => x.id === wantedConv);
    if (c) { setSelected(c); setPendingNew(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedConv, conversations]);

  useEffect(() => {
    if (!selected || !user) return;
    fetchMessages(selected.id);
    markAsRead(selected.id);
    setConversations(prev => prev.map(c => c.id === selected.id ? { ...c, unreadCount: 0 } : c));

    const channel = supabase
      .channel(`messages:${selected.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `conversation_id=eq.${selected.id}`,
      }, (payload) => {
        const msg = payload.new as Message;
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
        // Marca como lida automaticamente se o destinatario esta na conversa
        if (msg.sender_id !== user.id) {
          supabase.from("messages").update({ read: true }).eq("id", msg.id).then(announceRead);
          setConversations(prev => prev.map(cv =>
            cv.id === msg.conversation_id ? { ...cv, unreadCount: 0 } : cv
          ));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, user?.id]);

  const activeListingId = pendingNew ? pendingNew.listingId : selected?.listing_id;
  const activeListing = pendingNew ? pendingListing : selected?.listing;
  const activeOtherUser = pendingNew
    ? { id: pendingNew.sellerId, name: "" }
    : selected?.other_user || null;
  const otherId = activeOtherUser?.id;
  const otherIsSeller = pendingNew ? true : !!selected && selected.seller_id === otherId;

  // loja publica da outra pessoa (so aparece se ela tiver loja ativa)
  useEffect(() => {
    setStoreSlug(null);
    if (!otherId) return;
    let alive = true;
    supabase.rpc("store_slug_of", { p_user: otherId }).then(({ data }) => {
      if (alive) setStoreSlug(typeof data === "string" && data ? data : null);
    });
    return () => { alive = false; };
  }, [otherId]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" />;

  async function fetchConversations() {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("conversations")
      .select(`
        *,
        listing:listings(title, images, price, price_unit),
        buyer:profiles!conversations_buyer_id_fkey(id, name),
        seller:profiles!conversations_seller_id_fkey(id, name)
      `)
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
      .order("created_at", { ascending: false });

    if (data) {
      // Busca contagem de nao lidas para cada conversa
      const convIds = data.map((c: any) => c.id);
      const { data: unreadData } = await supabase
        .from("messages")
        .select("conversation_id")
        .in("conversation_id", convIds)
        .neq("sender_id", user.id)
        .or("read.eq.false,read.is.null");
      const unreadMap: Record<string, number> = {};
      (unreadData || []).forEach((m: any) => {
        unreadMap[m.conversation_id] = (unreadMap[m.conversation_id] || 0) + 1;
      });
      const convs = data.map((c: any) => ({
        ...c,
        listing: c.listing,
        other_user: user.id === c.buyer_id ? c.seller : c.buyer,
        unreadCount: unreadMap[c.id] || 0,
      }));
      setConversations(convs);

      if (newListingId && newSellerId) {
        const existing = convs.find((c) => c.listing_id === newListingId && c.buyer_id === user.id);
        if (existing) {
          setSelected(existing);
          setPendingNew(null);
        } else {
          const { data: listingData } = await supabase
            .from("listings")
            .select("title, images, price, price_unit")
            .eq("id", newListingId)
            .single();
          setPendingListing(listingData);
          setPendingNew({ listingId: newListingId, sellerId: newSellerId });
          setSelected(null);
        }
      } else if (convs.length > 0) {
        setSelected((cur) => convs.find((c: any) => c.id === (wantedConv || cur?.id)) || convs[0]);
      }
    }
    setLoading(false);
  }

  async function fetchMessages(conversationId: string) {
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });
    if (data) {
      setMessages(data);
      setTimeout(() => {
        if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      }, 50);
    }
  }

  async function markAsRead(conversationId: string) {
    if (!user) return;
    await supabase.from("messages").update({ read: true })
      .eq("conversation_id", conversationId).neq("sender_id", user.id);
    announceRead();
  }

  function openConversation(conv: Conversation) {
    setSelected(conv);
    setPendingNew(null);
    setSearchParams({ c: conv.id }, { replace: true });
  }

  async function sendMessage() {
    if (!user || !input.trim()) return;
    if (user.blocked) { alert("Sua conta está bloqueada e não pode enviar mensagens."); return; }
    const content = input.trim();
    setInput("");
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);

    if (pendingNew) {
      const { data: conv, error } = await supabase
        .from("conversations")
        .insert({ listing_id: pendingNew.listingId, buyer_id: user.id, seller_id: pendingNew.sellerId })
        .select(`*, listing:listings(title, images, price, price_unit), seller:profiles!conversations_seller_id_fkey(id, name)`)
        .single();

      if (error || !conv) return;
      const newConv = { ...conv, listing: conv.listing, other_user: conv.seller };
      setConversations((prev) => [newConv, ...prev]);
      setSelected(newConv);
      setPendingNew(null);
      setPendingListing(null);
      setSearchParams({ c: conv.id }, { replace: true });
      await supabase.from("messages").insert({ conversation_id: conv.id, sender_id: user.id, content });
      // Notificar vendedor (o toque no aviso abre esta conversa)
      supabase.functions.invoke("notify-new-message", {
        body: { recipient_id: conv.seller?.id, conversation_id: conv.id, message_preview: content },
      });
      return;
    }

    if (!selected) return;
    await supabase.from("messages").insert({ conversation_id: selected.id, sender_id: user.id, content });
    // Notificar outro usuário (o toque no aviso abre esta conversa)
    const otherUserId = selected.buyer_id === user.id ? selected.seller_id : selected.buyer_id;
    supabase.functions.invoke("notify-new-message", {
      body: { recipient_id: otherUserId, conversation_id: selected.id, message_preview: content },
    });
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const lastDealMsg: Record<string, string> = {};
  messages.forEach((m) => { if (m.kind === "deal_check" && m.meta?.deal_check_id) lastDealMsg[m.meta.deal_check_id] = m.id; });

  if (loading) return (
    <div className="container mx-auto px-4 py-20 text-center text-muted-foreground">Carregando mensagens...</div>
  );

  const hasContent = conversations.length > 0 || pendingNew;

  if (!hasContent) return (
    <div className="container mx-auto px-4 py-20 text-center">
      <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
      <p className="text-muted-foreground mb-4">Você ainda não tem conversas.</p>
      <Link to="/busca"><Button className="bg-primary text-primary-foreground">Buscar servicos</Button></Link>
    </div>
  );

  const other = activeOtherUser && (
    <>
      <div className="text-right min-w-0">
        <p className="text-xs text-muted-foreground">{otherIsSeller ? "Anunciante" : "Interessado"}</p>
        <p className="text-sm font-medium truncate max-w-[9rem] sm:max-w-none">{activeOtherUser.name}</p>
        {storeSlug && (
          <p className="flex items-center justify-end gap-1 text-[11px] font-semibold text-primary">
            <Store className="h-3 w-3" /> Ver loja
          </p>
        )}
      </div>
      <UserAvatar userId={activeOtherUser.id} name={activeOtherUser.name || ""} size="sm" />
    </>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <AppDownloadPrompt />
      <h1 className="text-2xl font-heading font-bold mb-6">Mensagens</h1>
      <div className="grid md:grid-cols-3 gap-4 h-[600px]">

        {/* Lista de conversas */}
        <div className="border border-border rounded-lg overflow-hidden flex flex-col">
          <div className="p-3 border-b border-border bg-secondary font-medium text-sm">
            Conversas ({conversations.length})
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => openConversation(conv)}
                className={`w-full text-left p-3 border-b border-border hover:bg-secondary transition-colors ${selected?.id === conv.id && !pendingNew ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={conv.listing?.images?.[0] || "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=60&h=60&fit=crop"}
                    alt="" className="h-10 w-10 rounded-md object-cover shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{conv.other_user?.name || "Usuario"}</p>
                    <p className="text-xs text-muted-foreground truncate">{conv.listing?.title}</p>
                  </div>
                  {conv.unreadCount ? (
                    <span className="shrink-0 bg-primary text-primary-foreground text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {conv.unreadCount > 9 ? "9+" : conv.unreadCount}
                    </span>
                  ) : null}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Area de chat */}
        <div className="md:col-span-2 border border-border rounded-lg overflow-hidden flex flex-col">
          {activeListing || pendingNew ? (
            <>
              {/* Cabecalho: anuncio a esquerda, outra pessoa a direita */}
              <div className="px-4 py-3 border-b border-border bg-secondary flex items-center justify-between gap-3">
                {/* Esquerda: info do anuncio */}
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={activeListing?.images?.[0] || "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=60&h=60&fit=crop"}
                    alt="" className="h-12 w-12 rounded-md object-cover shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{activeListing?.title || "Anuncio"}</p>
                    {activeListing?.price && (
                      <p className="text-xs text-primary font-medium">
                        R$ {activeListing.price}/{activeListing.price_unit}
                      </p>
                    )}
                    {activeListingId && (
                      <Link to={`/anuncio/${activeListingId}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                        <ExternalLink className="h-3 w-3" /> Ver anúncio
                      </Link>
                    )}
                  </div>
                </div>

                {/* Direita: a outra pessoa (foto e nome abrem a loja, se ela tiver uma) */}
                {activeOtherUser && (storeSlug ? (
                  <Link to={`/loja/${storeSlug}`} title="Ver loja"
                    className="flex items-center gap-2 shrink-0 rounded-lg p-1 -m-1 hover:bg-primary/10 transition-colors">
                    {other}
                  </Link>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">{other}</div>
                ))}
              </div>

              <div ref={messagesRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {pendingNew && (
                  <p className="text-center text-sm text-muted-foreground py-8">Envie uma mensagem para iniciar a conversa!</p>
                )}
                {!pendingNew && messages.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground py-8">Nenhuma mensagem ainda.</p>
                )}
                {messages.map((msg) => {
                  if (msg.kind === "deal_check" && msg.meta?.deal_check_id) {
                    if (lastDealMsg[msg.meta.deal_check_id] !== msg.id) return null;
                    return <div key={msg.id} className="mx-auto w-full max-w-sm"><DealCheckCard id={msg.meta.deal_check_id} /></div>;
                  }
                  if (msg.kind && msg.kind !== "text") {
                    return (
                      <div key={msg.id} className="flex justify-center">
                        <span className="rounded-full border border-[#ebe7dc] bg-white px-3 py-1.5 text-[11.5px] text-[#8a9088]">{msg.content}</span>
                      </div>
                    );
                  }
                  const isMe = msg.sender_id === user.id;
                  return (
                    <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm ${isMe ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                        <p>{msg.content}</p>
                        <div className={`flex items-center justify-end gap-1 mt-1`}>
                          <span className={`text-xs ${isMe ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                            {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {isMe && (
                            <span className={`text-xs ${msg.read ? "text-accent" : "text-primary-foreground/50"}`}>
                              {msg.read ? "✓✓" : "✓"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <div className="p-3 border-t border-border flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder="Digite uma mensagem..." className="flex-1" />
                <Button onClick={sendMessage} disabled={!input.trim()} className="bg-primary text-primary-foreground">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Selecione uma conversa
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
